import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminShell } from '@/apps/coach/components/admin/AdminShell';
import { routes } from '@/lib/routes';
import { PageHeader } from '@/components/layout/PageHeader';
import { api } from '@/lib/api';
import { Ticket, Users, BarChart3 } from 'lucide-react';

interface Stats {
  userCount: number;
  redemptionCount: number;
  voucherCount: number;
  totalUsage: number;
}

export function OperatorDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.operator.stats()
      .then(setStats)
      .catch((err) => {
        const msg = err instanceof Error ? err.message : '';
        if (msg.startsWith('API 401')) {
          navigate(routes.coach.welcome(), { replace: true });
          return;
        }
        setError(msg || 'Fehler beim Laden.');
      });
  }, [navigate]);

  if (error) {
    return (
      <AdminShell pageTitle="Plattform">
        <PageHeader title="Plattform" />
        <div className="flex-1 overflow-y-auto px-4 py-6">
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell pageTitle="Plattform">
      <PageHeader title="Plattform" />
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
        <section className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-border px-4 py-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Users size={14} /> User
            </div>
            <p className="text-2xl font-semibold mt-1">{stats?.userCount ?? '—'}</p>
          </div>
          <div className="rounded-xl border border-border px-4 py-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Ticket size={14} /> Einlösungen
            </div>
            <p className="text-2xl font-semibold mt-1">{stats?.redemptionCount ?? '—'}</p>
          </div>
          <div className="rounded-xl border border-border px-4 py-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Ticket size={14} /> Gutscheine
            </div>
            <p className="text-2xl font-semibold mt-1">{stats?.voucherCount ?? '—'}</p>
          </div>
          <div className="rounded-xl border border-border px-4 py-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <BarChart3 size={14} /> Nachrichten
            </div>
            <p className="text-2xl font-semibold mt-1">{stats?.totalUsage ?? '—'}</p>
          </div>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold">Bereiche</h2>
          <Link
            to={routes.coach.operator.vouchers()}
            className="block rounded-xl border border-border px-4 py-3 hover:bg-muted"
          >
            <p className="text-sm font-medium">Gutscheine verwalten</p>
            <p className="text-xs text-muted-foreground">Anlegen, auflisten, widerrufen</p>
          </Link>
          <Link
            to={routes.coach.operator.users()}
            className="block rounded-xl border border-border px-4 py-3 hover:bg-muted"
          >
            <p className="text-sm font-medium">Nutzer verwalten</p>
            <p className="text-xs text-muted-foreground">Alle registrierten Nutzer, Gutschein-Status</p>
          </Link>
          <Link
            to={routes.coach.operator.coaches()}
            className="block rounded-xl border border-border px-4 py-3 hover:bg-muted"
          >
            <p className="text-sm font-medium">Coach-Invites</p>
            <p className="text-xs text-muted-foreground">Einladungs-Codes für neue Coaches</p>
          </Link>
          <Link
            to={routes.coach.operator.analytics()}
            className="block rounded-xl border border-border px-4 py-3 hover:bg-muted"
          >
            <p className="text-sm font-medium">Analytics</p>
            <p className="text-xs text-muted-foreground">Tagesaktivität, Top-Nutzer, Voucher-Auslastung</p>
          </Link>
          <Link
            to={routes.coach.operator.announcements()}
            className="block rounded-xl border border-border px-4 py-3 hover:bg-muted"
          >
            <p className="text-sm font-medium">Ankündigungen</p>
            <p className="text-xs text-muted-foreground">Updates für alle Nutzer verfassen</p>
          </Link>
        </section>
      </div>
    </AdminShell>
  );
}
